// Supply Work Orders cluster (MyWorkOrders + WorkOrderDetail) strings — EN + TR.
//
// Wave-4 Day 4. Cross-page shared dict across:
// - features/supply/MyWorkOrders.tsx (WO list + persona-scoped table)
// - features/supply/WorkOrderDetail.tsx (561 LOC; STATUS_LABEL + NEXT_LABEL
//   module-level hardcoded TR → dict-driven; QC checklist + production update
//   form; state machine timeline; production photos sub-section)
//
// Mehmet / Hakan SO2 Production Partner persona. Supply IS the supplier
// perspective, so persona-facing TR-friendly copy preserved (NO seller-leak
// constraint here — Mehmet/Hakan are the audience, not unwanted references).
// Bilingual switch capable for Volkan A1 admin reviewing the supply surface.
//
// V1 supply-local; admin /admin/work-orders surface Wave-16 backlog →
// admin re-share extension via shared dict candidate Wave-16 paralel.
//
// Wave-1 helpers consume:
// - lib/datetime.ts (formatDate / formatDateTime) — date locale
//
// Status enum source: mocks/data/work-orders WorkOrderStatus (Phase 1 OM
// dual-path 2026-06-03):
//   hub_via_nj: assigned → acknowledged → in_production → dispatched_to_warehouse
//     → received_at_warehouse
//   direct_dropship: assigned → acknowledged → in_production → dispatched_to_customer
// cancelled = terminal off-ramp. QC removed as state (now in-production event).

import type { LocalizedDict } from '../index'

type SupplyWorkOrdersStrings = {
  myWorkOrders: {
    pageTitle: string
    table: {
      columnHeaders: {
        woId: string
        product: string
        qty: string
        customerOrderRef: string
        assignedAt: string
        status: string
      }
      emptyState: { title: string; description: string }
    }
    states: {
      loading: string
      error: string
    }
    actions: { refresh: string }
    mobile: {
      queueLabel: string
      queues: {
        all: string
        assigned: string
        active: string
        dispatched: string
        completed: string
      }
      count: (count: number) => string
      search: string
      closeSearch: string
      searchPlaceholder: string
      filters: string
      filterTitle: string
      cancel: string
      apply: string
      clear: string
      retry: string
      status: string
      allStatuses: string
      routeLabel: string
      allRoutes: string
      route: {
        hubViaNj: string
        directDropship: string
      }
      dueDate: string
      overdueOnly: string
      due: (value: string) => string
      overdue: (days: number) => string
      dueToday: string
      quantity: (count: number) => string
      openWorkOrder: (id: string) => string
    }
    desktop: {
      description: (displayName: string) => string
      total: string
      overdueMetric: (count: number) => string
      searchLabel: string
      searchPlaceholder: string
      reset: string
      columns: {
        route: string
        due: string
      }
    }
    roleState: {
      title: string
      description: (currentPersona: string) => string
      currentRole: string
      productionPartner: string
      openWorkspace: string
      backToOverview: string
    }
  }
  workOrderDetail: {
    pageTitle: (woNumber: string) => string
    pageDescription: string
    backLink: string
    nonSo2Gate: (orgName: string, orgType: string) => string
    loading: string
    errorPrefix: string
    unknownError: string
    listLink: string
    headerLine: {
      qtyUnits: (qty: number) => string
      customerRefLabel: string
    }
    panels: {
      summary: string
      stateMachine: string
      lifecycleHistory: string
      productionPhotos: (count: number) => string
      productionUpdates: string
      activityTimeline: string
      items: string
      qcChecklist: string
    }
    summaryFields: {
      assignedAtLabel: string
      dueLabel: string
      notesLabel: string
    }
    statusLabel: {
      assigned: string
      acknowledged: string
      inProduction: string
      dispatchedToWarehouse: string
      receivedAtWarehouse: string
      dispatchedToCustomer: string
      cancelled: string
    }
    nextActionLabel: {
      acknowledge: string
      startProduction: string
      dispatch: string
      markReceived: string
      terminal: string
    }
    transitionForm: {
      noteLabel: string
      notePlaceholder: string
      transitionPending: string
      errorPrefix: string
      dispatchViaPanel: string
    }
    terminalNote: string
    historyEmpty: string
    toasts: {
      transitionSuccessTitle: (statusLabel: string) => string
      transitionSuccessDesc: (product: string, qty: number) => string
      transitionAdminTitle: string
      transitionAdminDesc: (orgName: string, product: string, statusLabel: string) => string
      transitionErrorTitle: string
    }
    qcChecklist: {
      title: string
      addItem: string
      markComplete: string
      itemPlaceholder: string
    }
    productionUpdateForm: {
      title: string
      messageLabel: string
      messagePlaceholder: string
      attachmentsLabel: string
      submit: string
      submitting: string
      successToast: string
    }
    productionPhotos: {
      sectionHint: string
      uploadCta: string
      loading: string
      emptyState: string
      addingForm: {
        filenamePlaceholder: string
        stepLabelPlaceholder: string
        notesPlaceholder: string
        cancel: string
        upload: string
        uploading: string
      }
      orgsSharedSuffix: (n: number) => string
      successToast: string
      deleteTitle: string
    }
  }
}

export const SUPPLY_WORK_ORDERS_STRINGS: LocalizedDict<SupplyWorkOrdersStrings> = {
  en: {
    myWorkOrders: {
      pageTitle: 'My Work Orders',
      table: {
        columnHeaders: {
          woId: 'Work order',
          product: 'Product',
          qty: 'Qty',
          customerOrderRef: 'Order',
          assignedAt: 'Assigned',
          status: 'Status',
        },
        emptyState: {
          title: 'No work orders yet',
          description: 'No production work orders have been assigned to you yet.',
        },
      },
      states: {
        loading: 'Loading…',
        error: 'List could not be loaded.',
      },
      actions: { refresh: 'Refresh' },
      mobile: {
        queueLabel: 'Work order queue',
        queues: {
          all: 'All work orders',
          assigned: 'New assignments',
          active: 'In production',
          dispatched: 'Dispatched',
          completed: 'Completed',
        },
        count: (count) => `${count} work orders`,
        search: 'Search work orders',
        closeSearch: 'Close search',
        searchPlaceholder: 'Work order, product or order number…',
        filters: 'Filters',
        filterTitle: 'Filter work orders',
        cancel: 'Cancel',
        apply: 'Apply',
        clear: 'Clear filters',
        retry: 'Retry',
        status: 'Production status',
        allStatuses: 'All statuses',
        routeLabel: 'Shipping',
        allRoutes: 'All shipping',
        route: {
          hubViaNj: 'Via NJ warehouse',
          directDropship: 'To customer',
        },
        dueDate: 'Due date',
        overdueOnly: 'Overdue only',
        due: (value) => `Due ${value}`,
        overdue: (days) => `${days}d overdue`,
        dueToday: 'Due today',
        quantity: (count) => `${count} units`,
        openWorkOrder: (id) => `Open work order ${id}`,
      },
      desktop: {
        description: (displayName) =>
          `Production work orders assigned to ${displayName}.`,
        total: 'Total work orders',
        overdueMetric: (count) => `${count} overdue`,
        searchLabel: 'Search',
        searchPlaceholder: 'Work order, product or order number…',
        reset: 'Reset',
        columns: {
          route: 'Shipping',
          due: 'Due',
        },
      },
      roleState: {
        title: 'Production workspace required',
        description: (currentPersona) =>
          `Work Orders is available to SO2 Production Partners. ${currentPersona} is currently using an SO3 vendor workspace.`,
        currentRole: 'Current workspace',
        productionPartner: 'Production partner',
        openWorkspace: 'Open production workspace',
        backToOverview: 'Back to overview',
      },
    },
    workOrderDetail: {
      pageTitle: (woNumber) => `Work order ${woNumber}`,
      pageDescription: 'Production work order detail',
      backLink: '← My Work Orders',
      nonSo2Gate: (orgName, orgType) =>
        `Work orders are only for SO2 partner personas. Current: ${orgName} (${orgType}).`,
      loading: 'Loading…',
      errorPrefix: 'Detail could not be loaded:',
      unknownError: 'Unknown error',
      listLink: '← Back to list',
      headerLine: {
        qtyUnits: (qty) => `${qty} units`,
        customerRefLabel: 'Order:',
      },
      panels: {
        summary: 'Assignment details',
        stateMachine: 'Progress',
        lifecycleHistory: 'History',
        productionPhotos: (count) => `Production photos (${count})`,
        productionUpdates: 'Production updates',
        activityTimeline: 'Activity timeline',
        items: 'Items',
        qcChecklist: 'QC checklist',
      },
      summaryFields: {
        assignedAtLabel: 'Assigned at:',
        dueLabel: 'Due:',
        notesLabel: 'Assignment note:',
      },
      statusLabel: {
        assigned: 'Assigned',
        acknowledged: 'Acknowledged',
        inProduction: 'In production',
        dispatchedToWarehouse: 'Dispatched to warehouse',
        receivedAtWarehouse: 'Received at warehouse',
        dispatchedToCustomer: 'Dispatched to customer',
        cancelled: 'Cancelled',
      },
      nextActionLabel: {
        acknowledge: 'Acknowledge',
        startProduction: 'Start production',
        dispatch: 'Dispatch',
        markReceived: 'Mark received',
        terminal: '—',
      },
      transitionForm: {
        noteLabel: 'Note for this step (optional)',
        notePlaceholder: 'e.g. galvanizing finish OK, 2 units needed retouch',
        transitionPending: 'Saving…',
        errorPrefix: 'Error:',
        dispatchViaPanel:
          'To ship this work order, pass QC and then use Dispatch below: carrier and tracking number are required.',
      },
      terminalNote: 'Work order completed. There are no more steps.',
      historyEmpty: 'No steps recorded yet. The work order is still as it was assigned.',
      toasts: {
        transitionSuccessTitle: (statusLabel) => `Work order → ${statusLabel}`,
        transitionSuccessDesc: (product, qty) => `${product} · ${qty} units`,
        transitionAdminTitle: 'Work order updated',
        transitionAdminDesc: (orgName, product, statusLabel) =>
          `${orgName}: ${product} → ${statusLabel}`,
        transitionErrorTitle: 'Work order could not be updated',
      },
      qcChecklist: {
        title: 'QC checklist',
        addItem: 'Add item',
        markComplete: 'Mark complete',
        itemPlaceholder: 'e.g. Surface finish verified',
      },
      productionUpdateForm: {
        title: 'Send production update',
        messageLabel: 'Message',
        messagePlaceholder: 'e.g. Pallet 1/2 packed today, 2/2 tomorrow.',
        attachmentsLabel: 'Attachments',
        submit: 'Submit',
        submitting: 'Submitting…',
        successToast: 'Production update sent',
      },
      productionPhotos: {
        sectionHint:
          'Photos are automatically shared with admin + the relevant customer org (Drive cross-actor share).',
        uploadCta: 'Upload photo',
        loading: 'Loading…',
        emptyState:
          'No photos yet. You can attach photos to the WO record as production steps complete.',
        addingForm: {
          filenamePlaceholder: 'example-step3.jpg',
          stepLabelPlaceholder: "Step label (e.g. 'Step 3: Assembly')",
          notesPlaceholder: 'Note (optional)',
          cancel: 'Cancel',
          upload: 'Upload',
          uploading: 'Uploading…',
        },
        orgsSharedSuffix: (n) => `${n} org${n === 1 ? '' : 's'} shared`,
        successToast: 'Photo added',
        deleteTitle: 'Delete',
      },
    },
  },
  tr: {
    myWorkOrders: {
      pageTitle: 'İş Emirlerim',
      table: {
        columnHeaders: {
          woId: 'İş emri',
          product: 'Ürün',
          qty: 'Adet',
          customerOrderRef: 'Sipariş',
          assignedAt: 'Atama',
          status: 'Durum',
        },
        emptyState: {
          title: 'Henüz iş emri yok',
          description: 'Henüz iş emri atanmamış.',
        },
      },
      states: {
        loading: 'Yükleniyor…',
        error: 'Liste yüklenemedi.',
      },
      actions: { refresh: 'Yenile' },
      mobile: {
        queueLabel: 'İş emri kuyruğu',
        queues: {
          all: 'Tüm iş emirleri',
          assigned: 'Yeni atamalar',
          active: 'Üretimde',
          dispatched: 'Gönderildi',
          completed: 'Tamamlandı',
        },
        count: (count) => `${count} iş emri`,
        search: 'İş emri ara',
        closeSearch: 'Aramayı kapat',
        searchPlaceholder: 'İş emri, ürün veya sipariş no…',
        filters: 'Filtreler',
        filterTitle: 'İş emirlerini filtrele',
        cancel: 'Vazgeç',
        apply: 'Uygula',
        clear: 'Filtreleri temizle',
        retry: 'Tekrar dene',
        status: 'Üretim durumu',
        allStatuses: 'Tüm durumlar',
        routeLabel: 'Gönderim şekli',
        allRoutes: 'Tüm gönderimler',
        route: {
          hubViaNj: 'NJ depomuz üzerinden',
          directDropship: 'Doğrudan müşteriye',
        },
        dueDate: 'Termin tarihi',
        overdueOnly: 'Yalnız gecikenler',
        due: (value) => `Termin ${value}`,
        overdue: (days) => `${days} gün gecikti`,
        dueToday: 'Termin bugün',
        quantity: (count) => `${count} adet`,
        openWorkOrder: (id) => `${id} iş emrini aç`,
      },
      desktop: {
        description: (displayName) =>
          `${displayName} için atanmış üretim iş emirleri.`,
        total: 'Toplam iş emri',
        overdueMetric: (count) => `${count} geciken`,
        searchLabel: 'Ara',
        searchPlaceholder: 'İş emri, ürün veya sipariş no…',
        reset: 'Sıfırla',
        columns: {
          route: 'Gönderim',
          due: 'Termin',
        },
      },
      roleState: {
        title: 'Üretim çalışma alanı gerekli',
        description: (currentPersona) =>
          `İş Emirleri SO2 Üretim Partnerleri tarafından kullanılabilir. ${currentPersona} şu anda SO3 tedarikçi çalışma alanını kullanıyor.`,
        currentRole: 'Mevcut çalışma alanı',
        productionPartner: 'Üretim partneri',
        openWorkspace: 'Üretim çalışma alanını aç',
        backToOverview: 'Genel bakışa dön',
      },
    },
    workOrderDetail: {
      pageTitle: (woNumber) => `İş Emri ${woNumber}`,
      pageDescription: 'Üretim iş emri detayı',
      backLink: '← İş Emirlerim',
      nonSo2Gate: (orgName, orgType) =>
        `İş emirleri sadece SO2 partner persona'ları için. Şu an: ${orgName} (${orgType}).`,
      loading: 'Yükleniyor…',
      errorPrefix: 'Detay yüklenemedi:',
      unknownError: 'Bilinmeyen hata',
      listLink: '← Listeye dön',
      headerLine: {
        qtyUnits: (qty) => `${qty} adet`,
        customerRefLabel: 'Sipariş:',
      },
      panels: {
        summary: 'Atama detayları',
        stateMachine: 'İlerleme',
        lifecycleHistory: 'Geçmiş',
        productionPhotos: (count) => `Üretim Fotoğrafları (${count})`,
        productionUpdates: 'Üretim güncellemeleri',
        activityTimeline: 'Aktivite akışı',
        items: 'Kalemler',
        qcChecklist: 'QC kontrol listesi',
      },
      summaryFields: {
        assignedAtLabel: 'Atama tarihi:',
        dueLabel: 'Termin:',
        notesLabel: 'Atama notu:',
      },
      statusLabel: {
        assigned: 'Atandı',
        acknowledged: 'Onaylandı',
        inProduction: 'Üretimde',
        dispatchedToWarehouse: 'Depoya gönderildi',
        receivedAtWarehouse: 'Depoda teslim alındı',
        dispatchedToCustomer: 'Müşteriye gönderildi',
        cancelled: 'İptal edildi',
      },
      nextActionLabel: {
        acknowledge: 'Onayla',
        startProduction: 'Üretime başla',
        dispatch: 'Gönderime ver',
        markReceived: 'Depo teslim aldı',
        terminal: '—',
      },
      transitionForm: {
        noteLabel: 'Bu adım için not (isteğe bağlı)',
        notePlaceholder: 'Örn: galvaniz kaplama OK, 2 adet retouch gerekti',
        transitionPending: 'Kaydediliyor…',
        errorPrefix: 'Hata:',
        dispatchViaPanel:
          'Bu iş emrini göndermek için kalite kontrolü geçin, sonra aşağıdaki Gönderim bölümünü kullanın: kargo firması ve takip kodu zorunlu.',
      },
      terminalNote: 'İş emri tamamlandı. Başka adım kalmadı.',
      historyEmpty: 'Henüz kayıtlı adım yok. İş emri atandığı gibi duruyor.',
      toasts: {
        transitionSuccessTitle: (statusLabel) => `İş emri → ${statusLabel}`,
        transitionSuccessDesc: (product, qty) => `${product} · ${qty} adet`,
        transitionAdminTitle: 'İş emri güncellendi',
        transitionAdminDesc: (orgName, product, statusLabel) =>
          `${orgName}: ${product} → ${statusLabel}`,
        transitionErrorTitle: 'İş emri güncellenemedi',
      },
      qcChecklist: {
        title: 'QC kontrol listesi',
        addItem: 'Madde ekle',
        markComplete: 'Tamamlandı işaretle',
        itemPlaceholder: 'Örn: Yüzey kaplaması kontrol edildi',
      },
      productionUpdateForm: {
        title: 'Üretim güncellemesi gönder',
        messageLabel: 'Mesaj',
        messagePlaceholder: 'Örn: Palet 1/2 bugün paketlendi, 2/2 yarın.',
        attachmentsLabel: 'Ekler',
        submit: 'Gönder',
        submitting: 'Gönderiliyor…',
        successToast: 'Üretim güncellemesi gönderildi',
      },
      productionPhotos: {
        sectionHint:
          "Fotoğrafların admin + ilgili müşteri org'una otomatik paylaşılır (Drive cross-actor share).",
        uploadCta: 'Foto yükle',
        loading: 'Yükleniyor…',
        emptyState:
          'Henüz fotoğraf yok. Üretim adımı tamamlandıkça WO kaydına foto ekleyebilirsin.',
        addingForm: {
          filenamePlaceholder: 'ornek-step3.jpg',
          stepLabelPlaceholder: "Step etiketi (örn 'Step 3: Montaj')",
          notesPlaceholder: 'Not (opsiyonel)',
          cancel: 'Vazgeç',
          upload: 'Yükle',
          uploading: 'Ekleniyor…',
        },
        orgsSharedSuffix: (n) => `${n} org paylaşıldı`,
        successToast: 'Fotoğraf eklendi',
        deleteTitle: 'Sil',
      },
    },
  },
}
